<template>
    <div class="editor br rounded-ittybitty" v-bind:style="editorStyle">
        <textarea v-bind:id="id" v-bind:name="name" v-model="content" ref="editor"></textarea>
    </div>
</template>
<script>
import { EventBus } from '../../event-bus.js';

/**
 * Tailwind equivalent of the ".rte-button" email styles, used when the content
 * is rendered on the website. Keep in sync with the "safelist" in the website
 * project's tailwind.config.js since these classes only exist in stored content.
 */
const LINK_BUTTON_TAILWIND_CLASSES = 'inline-block box-border m-0 cursor-pointer rounded-full border border-solid border-theme bg-theme px-9 py-2.5 text-sm font-bold !text-white !no-underline hover:!text-white hover:!no-underline';

export default {
    props: {
        id: {
            type: String,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        content: {
            type: String
        },
        variables: {
            type: Object,
            default: null,
        },
        fixedToolbar: {
            type: Boolean,
            default: true,
        },
        formatting: {
            type: Array,
            default: () => ['p', 'h2', 'h3'],
        },
        plugins: {
            type: Array,
            default: () => ['alignment'],
        },
        buttons: {
            type: Array,
            default: () => ['format', 'bold', 'italic', 'underline', 'lists', 'image', 'file', 'link', 'horizontalrule', 'emoji'],
        },
        height: {
            type: String,
            default: '300px',
        },
        pastePlainText: {
            type: Boolean,
            default: false,
        },
        linkButtons: {
            type: Boolean,
            default: true,
        },
        linkButtonColor: {
            type: String,
            default: () => window.Kourses.themeColor,
        },
    },
    data() {
        return {
            editor: null,
        }
    },
    computed: {
        variablesPluginEnabled() {
            return typeof this.variables === 'object' && this.variables !== null;
        },
        editorStyle() {
            return this.linkButtons ? { '--rte-button-color': this.linkButtonColor } : {};
        },
    },
    async mounted() {
        await import('../../vendor/redactor.js');

        let pluginImports = {
            alignment: () => import('../../vendor/redactor-plugins/alignment.js'),
            video: () => import('../../vendor/redactor-plugins/video.js'),
            emoji: () => import('../../vendor/redactor-plugins/emoji.js'),
            code: () => import('../../vendor/redactor-plugins/code.js'),
            'custom-variables': () => import('../../vendor/redactor-plugins/custom-variables.js'),
        };

        let neededPlugins = new Set(this.plugins.filter(plugin => plugin in pluginImports));
        if (this.variablesPluginEnabled) {
            neededPlugins.add('custom-variables');
        }

        await Promise.all([...neededPlugins].map(plugin => pluginImports[plugin]()));

        if (!this.$el) return;

        let $vm = this;

        $R.addLang('en', {
            'link-display-as-button': this.trans('Display as a button'),
        });

        this.editor = $R($vm.$refs.editor, {
            plugins: this.variablesPluginEnabled ? this.plugins.concat(['custom-variables']) : this.plugins,
            variables: this.variablesPluginEnabled ? this.variables : [],
            buttons: this.buttons,
            formatting: this.formatting,
            imageUpload: window.Kourses.activeBaseUrl + '/upload/redactor?_token=' + window.Laravel.csrfToken,
            imageResizable: true,
            imagePosition: true,
            maxHeight: this.height,
            minHeight: this.height,
            toolbarFixed: this.fixedToolbar,
            callbacks: {
                changed(html) {
                    $vm.$emit('input', html);
                    // Even though it seems like we could use
                    // `this.storage.getChanges()` and remove all images
                    // that were removed it should not be done. Why? Because
                    // user might not save changes and you would delete
                    // their changes. Better way (and this is what we ended
                    // implementing) is to use bootables and updated hook
                    // to check if RTE field changed and get image
                    // differences.
                    EventBus.$emit('form:element:changed', $vm.name, html);
                }
            },
            linkNewTab: true,
            linkTitle: true,
            linkButton: this.linkButtons,
            linkButtonExtraClass: LINK_BUTTON_TAILWIND_CLASSES,
            pastePlainText: this.pastePlainText,
        });
    }
};
</script>